// iot-admin/client/src/pages/Drivers.js
//
// Drivers as a top-level section — the people, not the assignments.
//
// ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────
// Until V249 a "driver" was a row on device_drivers, one per assignment, so the
// only way to reach one was through a device. That is also what made the device
// page dangerous: with no notion of a person, editing a name there looked local
// when it was global.
//
// This is the other half of that fix. Questions that are about a HUMAN and not
// about a cab now have somewhere to live:
//
//   who do we have, and who is idle?          -> status filter
//   whose KYC is missing?                     -> kyc filter (the compliance list)
//   has this person driven more than one cab? -> assignments column
//
// ── WHAT THIS SCREEN CAN AND CANNOT DO ──────────────────────────────────────
// Create, yes: onboarding a driver before a cab exists for them is a real thing
// an operator does, and forcing it through a device page is why we had rows of
// half-typed duplicate humans in the first place.
//
// Assign, no. Assignment is a fact about a CAB, so it belongs on the device page
// where the cab is in front of you. Edit, no — that lives on the person's detail
// screen, where the blast radius ("this renames them on every cab") can be
// stated next to the button. A listing that could do either would repeat the
// "one control, two meanings" mistake in a new place.

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { driverService } from '../services/api';
import '../components/DeviceSimTaxiDriver.css'; // std-input / std-muted / std-form-actions

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  const days = Math.floor((Date.now() - date) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Drivers = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newDriver, setNewDriver] = useState({ name: '', mobile: '' });
  const [createError, setCreateError] = useState(null);
  // When create is refused because the mobile is already someone's, we hold that
  // someone's id so the admin can be sent to them instead of being told "no".
  const [duplicateId, setDuplicateId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: 'all', kyc: 'all' });
  const [sorting, setSorting] = useState({ sortBy: 'active', sortOrder: 'desc' });

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await driverService.list({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        status: filters.status,
        kyc: filters.kyc,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      setDrivers(res.data || []);
      if (res.pagination) setPagination((p) => ({ ...p, ...res.pagination }));
      setError(null);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters.search, filters.status, filters.kyc, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const setFilter = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const closeCreate = () => {
    setCreating(false);
    setNewDriver({ name: '', mobile: '' });
    setCreateError(null);
    setDuplicateId(null);
  };

  const submitCreate = async () => {
    if (newDriver.name.trim().length < 2) { setCreateError('Name must be at least 2 characters'); return; }
    setSaving(true);
    setCreateError(null);
    setDuplicateId(null);
    try {
      const res = await driverService.create({
        name: newDriver.name.trim(),
        mobile: newDriver.mobile.trim() || null,
      });
      // Straight to the person: the next thing an admin wants is their detail
      // screen, not to hunt for them in a list they just added to.
      navigate(`/drivers/${res.data.id}`);
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Failed to create driver');
      setDuplicateId(e.response?.data?.data?.existingDriverId || null);
      setSaving(false);
    }
  };

  const handleSort = (field) => {
    setSorting((prev) => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };
  const sortIcon = (f) => (sorting.sortBy !== f ? '↕' : sorting.sortOrder === 'asc' ? '↑' : '↓');

  // KYC is shown per-document rather than as one boolean: "has some documents"
  // is not a useful compliance answer when what is missing is the licence.
  const kycChips = (d) => {
    const items = [
      ['Aadhaar', d.kyc?.aadhaar], ['PAN', d.kyc?.pan], ['Licence', d.kyc?.license],
    ];
    if (items.every(([, v]) => !v)) {
      return <span className="status-badge status-disabled" title="No identity documents on file">None</span>;
    }
    return (
      <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {items.map(([label, present]) => (
          <span
            key={label}
            className={`status-badge ${present ? 'status-active' : 'status-pending'}`}
            title={present ? `${label} on file` : `${label} missing`}
            style={{ fontSize: '0.7rem' }}
          >
            {present ? '✓' : '○'} {label}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>🧑‍✈️ Drivers</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New driver</button>
      </div>

      {error && <div className="alert alert-error">❌ {error}</div>}

      {creating && (
        <div className="detail-section">
          <div className="section-header"><h2>New driver</h2></div>
          <p className="std-muted">
            Creates the person only. Put them in a cab from that cab&rsquo;s device page —
            a driver can exist here with no vehicle.
          </p>
          {createError && (
            <div className="alert alert-error">
              ❌ {createError}
              {duplicateId && (
                <>
                  {' '}
                  <Link to={`/drivers/${duplicateId}`} className="link-button">Open that driver →</Link>
                </>
              )}
            </div>
          )}
          <div className="detail-grid">
            <div className="detail-item">
              <label>Name</label>
              <input className="std-input" value={newDriver.name} autoFocus
                onChange={(e) => setNewDriver((d) => ({ ...d, name: e.target.value }))} />
            </div>
            <div className="detail-item">
              <label>Mobile</label>
              <input className="std-input" value={newDriver.mobile} placeholder="+91…"
                onChange={(e) => setNewDriver((d) => ({ ...d, mobile: e.target.value }))} />
              <small className="std-muted">
                Optional, but it is what identifies this person. Without it, the same
                driver typed twice becomes two records.
              </small>
            </div>
            <div className="detail-item std-form-actions">
              <button className="btn btn-primary btn-sm" onClick={submitCreate} disabled={saving}>
                {saving ? 'Creating…' : '💾 Create driver'}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={closeCreate} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by name or mobile…"
            value={filters.search}
            onChange={(e) => setFilter({ search: e.target.value })}
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value })} title="Currently driving a cab?">
          <option value="all">All drivers</option>
          <option value="assigned">🚕 Currently driving</option>
          <option value="unassigned">Idle (no active cab)</option>
        </select>
        <select value={filters.kyc} onChange={(e) => setFilter({ kyc: e.target.value })} title="Identity documents on file">
          <option value="all">All KYC</option>
          <option value="missing">⚠️ No documents</option>
          <option value="present">Has documents</option>
        </select>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>Driver {sortIcon('name')}</th>
                  <th>Mobile</th>
                  <th className="sortable" onClick={() => handleSort('active')}>Currently driving {sortIcon('active')}</th>
                  <th className="sortable" onClick={() => handleSort('assignments')}>Cabs {sortIcon('assignments')}</th>
                  <th>Documents</th>
                  <th className="sortable" onClick={() => handleSort('lastAssigned')}>Last assigned {sortIcon('lastAssigned')}</th>
                </tr>
              </thead>
              <tbody>
                {drivers.length === 0 ? (
                  <tr><td colSpan="6" className="empty-state">No drivers match these filters.</td></tr>
                ) : (
                  drivers.map((d) => (
                    <tr key={d.id}>
                      <td className="user-cell">
                        <div className="user-avatar">🧑‍✈️</div>
                        <div className="user-info">
                          <Link to={`/drivers/${d.id}`} className="user-name" style={{ color: '#1976d2', textDecoration: 'none' }}>
                            {d.name}
                          </Link>
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{d.mobile || '—'}</td>
                      <td>
                        {d.activeAssignments > 0 ? (
                          <span className="status-badge status-active">
                            {d.activeCab || 'assigned'}
                            {d.activeAssignments > 1 && ` +${d.activeAssignments - 1}`}
                          </span>
                        ) : (
                          <span className="status-badge">Idle</span>
                        )}
                      </td>
                      {/* Total assignments ever — the number that only became
                          answerable once the person became an entity. */}
                      <td>{d.assignments}</td>
                      <td>{kycChips(d)}</td>
                      <td>{fmtDate(d.lastAssignedAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}>← Prev</button>
              <span style={{ margin: '0 12px' }}>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalCount} drivers
              </span>
              <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Drivers;
